package com.tovtalk.ui.screen.settings

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AlternateEmail
import androidx.compose.material.icons.filled.ChatBubble
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.automirrored.filled.Message
import androidx.compose.material.icons.filled.PersonAdd
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.tovtalk.data.local.TokenManager
import com.tovtalk.di.koinInject
import com.tovtalk.di.disposableKoinInject
import com.tovtalk.domain.entity.User
import com.tovtalk.presentation.state.UiState
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.ui.components.TovTalkButton
import com.tovtalk.ui.components.TovTalkButtonSize
import com.tovtalk.ui.components.TovTalkButtonVariant
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.presentation.viewmodel.profile.ProfileViewModel
import com.tovtalk.utils.strings.StringProvider

@Composable
fun NotificationSettingsScreen(
    onNavigateBack: () -> Unit,
    onNavigateToLogin: (() -> Unit)? = null,
    viewModel: ProfileViewModel = disposableKoinInject()
) {
    val authState by TokenManager.authStateFlow.collectAsState()
    val isGuestMode = authState.isGuestMode

    if (isGuestMode) {
        ScreenScaffold(
            topBar = {
                TovTalkTopBar(
                    title = StringProvider.get("notification_settings_title"),
                    onBack = onNavigateBack,
                )
            },
            constrainContentWidth = false,
        ) { paddingValues ->
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
                    .padding(24.dp),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = StringProvider.get("profile_guest_subtitle"),
                    style = TovTalkTheme.typography.bodyLarge,
                    color = TovTalkTheme.colors.textPrimary
                )
                Spacer(modifier = Modifier.height(24.dp))
                onNavigateToLogin?.let { onLogin ->
                    TovTalkButton(
                        text = StringProvider.get("auth_login_button"),
                        onClick = onLogin,
                        modifier = Modifier.fillMaxWidth(),
                        variant = TovTalkButtonVariant.Primary,
                        size = TovTalkButtonSize.Large
                    )
                }
            }
        }
        return
    }

    val userState by viewModel.userState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.loadUser(null)
    }

    ScreenScaffold(
        topBar = {
            TovTalkTopBar(
                title = StringProvider.get("notification_settings_title"),
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        val notificationSettingsContent = @Composable {
        when (val state = userState) {
            is UiState.Loading -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }
            }
            is UiState.Success<User> -> {
                NotificationSettingsContent(
                    user = state.data,
                    onPreferenceChange = { key, value -> viewModel.updateNotificationPreference(key, value) },
                    modifier = Modifier.padding(paddingValues)
                )
            }
            is UiState.Error -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentAlignment = Alignment.Center
                ) {
                    Text(text = state.message, color = TovTalkTheme.colors.error)
                }
            }
            else -> {}
        }
        }
        CenteredContentWrapper { notificationSettingsContent() }
    }
}

@Composable
private fun NotificationSettingsContent(
    user: User,
    onPreferenceChange: (String, Boolean) -> Unit,
    modifier: Modifier = Modifier
) {
    val screenPadding = TovTalkTheme.spacing.screenPadding

    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = screenPadding)
    ) {
        // Activity section header
        Text(
            text = StringProvider.get("notification_settings_activity"),
            style = TovTalkTheme.typography.labelMedium,
            color = TovTalkTheme.colors.textSecondary,
            fontWeight = FontWeight.Medium,
            modifier = Modifier.padding(top = 20.dp, bottom = 6.dp)
        )

        if (!user.isReadOnly) {
            NotificationSettingItem(
                title = StringProvider.get("notification_settings_likes"),
                description = StringProvider.get("notification_settings_likes_desc"),
                icon = Icons.Default.Favorite,
                isEnabled = user.notifyPostLike,
                onToggle = { onPreferenceChange("notify_post_like", it) }
            )
            HorizontalDivider(
                thickness = 0.5.dp,
                color = TovTalkTheme.colors.textTertiary.copy(alpha = 0.2f),
                modifier = Modifier.padding(start = 46.dp)
            )
            NotificationSettingItem(
                title = StringProvider.get("notification_settings_comments"),
                description = StringProvider.get("notification_settings_comments_desc"),
                icon = Icons.Default.ChatBubble,
                isEnabled = user.notifyPostComment,
                onToggle = { onPreferenceChange("notify_post_comment", it) }
            )
            HorizontalDivider(
                thickness = 0.5.dp,
                color = TovTalkTheme.colors.textTertiary.copy(alpha = 0.2f),
                modifier = Modifier.padding(start = 46.dp)
            )
        }
        NotificationSettingItem(
            title = StringProvider.get("notification_settings_mentions"),
            description = StringProvider.get("notification_settings_mentions_desc"),
            icon = Icons.Default.AlternateEmail,
            isEnabled = user.notifyMention,
            onToggle = { onPreferenceChange("notify_mention", it) }
        )
        if (!user.isReadOnly) {
            HorizontalDivider(
                thickness = 0.5.dp,
                color = TovTalkTheme.colors.textTertiary.copy(alpha = 0.2f),
                modifier = Modifier.padding(start = 46.dp)
            )
            NotificationSettingItem(
                title = StringProvider.get("notification_settings_followers"),
                description = StringProvider.get("notification_settings_followers_desc"),
                icon = Icons.Default.PersonAdd,
                isEnabled = user.notifyFollow,
                onToggle = { onPreferenceChange("notify_follow", it) }
            )
        }

        // Messages section header
        Text(
            text = StringProvider.get("notification_settings_messages"),
            style = TovTalkTheme.typography.labelMedium,
            color = TovTalkTheme.colors.textSecondary,
            fontWeight = FontWeight.Medium,
            modifier = Modifier.padding(top = 20.dp, bottom = 6.dp)
        )

        NotificationSettingItem(
            title = StringProvider.get("notification_settings_private"),
            description = StringProvider.get("notification_settings_private_desc"),
            icon = Icons.AutoMirrored.Filled.Message,
            isEnabled = user.notifyChat,
            onToggle = { onPreferenceChange("notify_chat", it) }
        )
        HorizontalDivider(
            thickness = 0.5.dp,
            color = TovTalkTheme.colors.textTertiary.copy(alpha = 0.2f),
            modifier = Modifier.padding(start = 46.dp)
        )
        NotificationSettingItem(
            title = StringProvider.get("notification_settings_message_requests"),
            description = StringProvider.get("notification_settings_message_requests_desc"),
            icon = Icons.Default.ChatBubble,
            isEnabled = user.notifyMessageRequests,
            onToggle = { onPreferenceChange("notify_message_requests", it) }
        )

        Spacer(modifier = Modifier.height(24.dp))
        Text(
            text = StringProvider.get("notification_settings_note"),
            style = TovTalkTheme.typography.bodySmall,
            color = TovTalkTheme.colors.textSecondary,
            modifier = Modifier.padding(horizontal = 8.dp)
        )
        Spacer(modifier = Modifier.height(16.dp))
    }
}

@Composable
private fun NotificationSettingItem(
    title: String,
    description: String,
    icon: ImageVector,
    isEnabled: Boolean,
    onToggle: (Boolean) -> Unit
) {
    var checked by remember(isEnabled) { mutableStateOf(isEnabled) }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable {
                checked = !checked
                onToggle(checked)
            }
            .padding(horizontal = 0.dp, vertical = 13.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            tint = TovTalkTheme.colors.textTertiary,
            modifier = Modifier.size(22.dp)
        )
        Spacer(modifier = Modifier.width(16.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = title,
                style = TovTalkTheme.typography.bodyLarge,
                color = TovTalkTheme.colors.textPrimary,
                fontWeight = FontWeight.Medium
            )
            Text(
                text = description,
                style = TovTalkTheme.typography.bodySmall,
                color = TovTalkTheme.colors.textSecondary
            )
        }
        Spacer(modifier = Modifier.width(8.dp))
        Switch(
            checked = checked,
            onCheckedChange = { newValue ->
                checked = newValue
                onToggle(newValue)
            },
            colors = SwitchDefaults.colors(
                checkedThumbColor = TovTalkTheme.colors.brand,
                checkedTrackColor = TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.half)
            )
        )
    }
}
